'use client';

import { c as _c } from "react/compiler-runtime";
import { OTPInput, OTPInputContext } from 'input-otp';
import { Dot } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';
const InputOTP = React.forwardRef<React.ElementRef<typeof OTPInput>, React.ComponentPropsWithoutRef<typeof OTPInput>>((t0, ref) => {
  const $ = _c(13);
  let className;
  let containerClassName;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      containerClassName,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = containerClassName;
    $[3] = props;
  } else {
    className = $[1];
    containerClassName = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== containerClassName) {
    t1 = cn("flex items-center gap-2 has-disabled:opacity-50", containerClassName);
    $[4] = containerClassName;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== className) {
    t2 = cn("disabled:cursor-not-allowed", className);
    $[6] = className;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== props || $[9] !== ref || $[10] !== t1 || $[11] !== t2) {
    t3 = <OTPInput ref={ref} containerClassName={t1} className={t2} {...props} />;
    $[8] = props;
    $[9] = ref;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
});
InputOTP.displayName = 'InputOTP';
const InputOTPGroup = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex items-center", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <div ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
InputOTPGroup.displayName = 'InputOTPGroup';
const InputOTPSlot = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'> & {
  index: number;
  caretClassName?: string;
}>((t0, ref) => {
  const $ = _c(17);
  let caretClassName;
  let className;
  let index;
  let props;
  if ($[0] !== t0) {
    ({
      index,
      className,
      caretClassName,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = caretClassName;
    $[2] = className;
    $[3] = index;
    $[4] = props;
  } else {
    caretClassName = $[1];
    className = $[2];
    index = $[3];
    props = $[4];
  }
  const inputOTPContext = React.useContext(OTPInputContext);
  const {
    char,
    hasFakeCaret,
    isActive
  } = inputOTPContext.slots[index];
  const t1 = isActive && "ring-ring z-interactive ring-2";
  let t2;
  if ($[5] !== className || $[6] !== t1) {
    t2 = cn("relative flex h-10 w-10 items-center justify-center text-sm transition-all", t1, className);
    $[5] = className;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== caretClassName || $[9] !== hasFakeCaret) {
    t3 = hasFakeCaret && <div className="pointer-events-none absolute inset-0 flex items-center justify-center"><div className={cn("animate-caret-blink bg-foreground h-4 w-px duration-1000", caretClassName)} /></div>;
    $[8] = caretClassName;
    $[9] = hasFakeCaret;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== char || $[12] !== props || $[13] !== ref || $[14] !== t2 || $[15] !== t3) {
    t4 = <div ref={ref} className={t2} {...props}>{char}{t3}</div>;
    $[11] = char;
    $[12] = props;
    $[13] = ref;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
});
InputOTPSlot.displayName = 'InputOTPSlot';
const InputOTPSlotHide = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'> & {
  index: number;
  caretClassName?: string;
  dotClassName?: string;
}>((t0, ref) => {
  const $ = _c(23);
  let caretClassName;
  let className;
  let dotClassName;
  let index;
  let props;
  if ($[0] !== t0) {
    ({
      index,
      className,
      caretClassName,
      dotClassName,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = caretClassName;
    $[2] = className;
    $[3] = dotClassName;
    $[4] = index;
    $[5] = props;
  } else {
    caretClassName = $[1];
    className = $[2];
    dotClassName = $[3];
    index = $[4];
    props = $[5];
  }
  const inputOTPContext = React.useContext(OTPInputContext);
  const {
    char,
    hasFakeCaret,
    isActive
  } = inputOTPContext.slots[index];
  const t1 = isActive && "ring-ring z-interactive ring-2";
  let t2;
  if ($[6] !== className || $[7] !== t1) {
    t2 = cn("relative flex h-10 w-10 items-center justify-center text-sm transition-all", t1, className);
    $[6] = className;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = char && "bg-vive-white";
  let t4;
  if ($[9] !== dotClassName || $[10] !== t3) {
    t4 = cn("bg-vive-primary-50 rounded-full", dotClassName, t3);
    $[9] = dotClassName;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = <div className={t4} />;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== caretClassName || $[15] !== hasFakeCaret) {
    t6 = hasFakeCaret && <div className="pointer-events-none absolute inset-0 flex items-center justify-center"><div className={cn("animate-caret-blink bg-foreground h-4 w-px duration-1000", caretClassName)} /></div>;
    $[14] = caretClassName;
    $[15] = hasFakeCaret;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== props || $[18] !== ref || $[19] !== t2 || $[20] !== t5 || $[21] !== t6) {
    t7 = <div ref={ref} className={t2} {...props}>{t5}{t6}</div>;
    $[17] = props;
    $[18] = ref;
    $[19] = t2;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
});
InputOTPSlotHide.displayName = 'InputOTPSlotHide';
const InputOTPSeparator = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>((t0, ref) => {
  const $ = _c(6);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Dot />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props || $[4] !== ref) {
    t2 = <div ref={ref} role="separator" {...props}>{t1}</div>;
    $[3] = props;
    $[4] = ref;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
});
InputOTPSeparator.displayName = 'InputOTPSeparator';
export { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot, InputOTPSlotHide };